import { activeDict, activeDictLang } from "@/lib/i18n-dict";
import { FAQS, faqsForPage, type FaqEntry, type FaqPageKey } from "@/lib/mock/faqs";
import { SITE_URL } from "@/lib/utils/site-url";

/**
 * FAQPage structured data.
 *
 * Built in head(), which runs outside React, so the questions are read through
 * activeDict() — the same SSR bridge the glossary schema uses — and the markup
 * carries the active locale rather than always English.
 *
 * Two rules this file exists to keep:
 *
 *  1. The markup must describe questions the page actually renders. Both the
 *     schema and <FaqSection> read faqsForPage(), so they cannot drift apart.
 *  2. One FAQPage per page. Every route emits this under the id "ld-faq", which
 *     also makes client-side navigation replace the previous route's block
 *     instead of appending a second one.
 *
 * Worth being straight about the payoff: since 2023 Google shows FAQ rich
 * results only for authoritative government and health sites, so this will not
 * change how the page looks in search. It buys machine-readable answers for
 * answer engines and assistants, which is a different and slower-moving bet.
 */
function entrySchema(entry: FaqEntry, dict: Record<string, string>, pageUrl: string) {
  return {
    "@type": "Question",
    "@id": `${pageUrl}#faq-${entry.slug}`,
    name: dict[`faq.${entry.slug}.q`] || entry.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: dict[`faq.${entry.slug}.a`] || entry.a,
    },
  };
}

function pageUrlFor(path: string): string {
  const lang = activeDictLang() ?? "en";
  const prefix = lang === "en" ? "" : `/${lang}`;
  return `${SITE_URL}${prefix}${path === "/" ? "" : path}`;
}

function faqPageSchema(entries: FaqEntry[], path: string) {
  const dict = activeDict();
  const url = pageUrlFor(path);
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `${url}#faq`,
    url,
    inLanguage: activeDictLang() ?? "en",
    mainEntity: entries.map((e) => entrySchema(e, dict, url)),
  };
}

/**
 * head() script entry for a page's FAQ block. `path` is the English route path;
 * the locale prefix is added here so the @id is self-referencing per locale.
 */
export function faqHeadScript(page: FaqPageKey, path: string) {
  return {
    id: "ld-faq",
    type: "application/ld+json",
    children: JSON.stringify(faqPageSchema(faqsForPage(page), path)),
  };
}

/** head() script entry for the /faqs hub, which publishes every entry. */
export function faqHubHeadScript() {
  return {
    id: "ld-faq",
    type: "application/ld+json",
    children: JSON.stringify(faqPageSchema(FAQS, "/faqs")),
  };
}

/**
 * head() script entry for question sets that are not in the shared knowledge
 * base — product detail pages, whose FAQs are written per product.
 */
export function faqHeadScriptFromPairs(pairs: { q: string; a: string }[], path: string) {
  const url = pageUrlFor(path);
  return {
    id: "ld-faq",
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "@id": `${url}#faq`,
      url,
      inLanguage: activeDictLang() ?? "en",
      mainEntity: pairs.map((p, i) => ({
        "@type": "Question",
        "@id": `${url}#faq-${i}`,
        name: p.q,
        acceptedAnswer: { "@type": "Answer", text: p.a },
      })),
    }),
  };
}
